import { ZfIconButton, ZfLoadingSpinner, ZfRadio, ZfSelectionControlGroup, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate, useParams } from 'react-router';
import { useReorderTopic, useTopicDetails, useUpdateTopic } from '../../api';
import type { TalkingPointDto } from '../../api/generated/client';
import { AdminContentBox, AdminTopicEditList, AdminTopicSortList } from '../../components/admin';
import { EditTopicNameModal } from '../../components/modal';

export const AdminRootTopicEditor: FunctionComponent = () => {
    const { i18n, t } = useTranslation();
    const { topicId } = useParams();
    const { mutate: updateTopic } = useUpdateTopic();

    const navigate = useNavigate();
    const { data: topic } = useTopicDetails(topicId ? +topicId : undefined);
    const { mutate: reorderTopic } = useReorderTopic();

    const [unsavedTopic, setUnsavedTopic] = useState(topic);
    const [editorMode, setEditorMode] = useState('edit');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showRootNameModal, setShowRootNameModal] = useState(false);

    useEffect(() => setUnsavedTopic(topic), [topic, reorderTopic]);

    const onTopicChange = (reorderedTalkingPoints: TalkingPointDto[]) => {
        setUnsavedTopic({ ...unsavedTopic!, talkingPoints: reorderedTalkingPoints });
        setHasUnsavedChanges(true);
    };

    if (!topic) return <ZfLoadingSpinner style={{ margin: 'auto' }} />;

    return (
        <AdminContentBox>
            <ZfStack direction="column" spacing="medium">
                <ZfTypography variant="heading-small" style={{ cursor: 'pointer' }}>
                    <ZfStack direction="row" alignment="center">
                        <label>{t('contentAdmin.rootTopicLabel')}</label>
                        <b style={{ marginLeft: 'var(--padding-right-xs)' }}>{topic.name[i18n.language]}</b>
                        <ZfIconButton icon="edit" hierarchy="tertiary" size="large" onClick={() => setShowRootNameModal(true)} />
                    </ZfStack>
                </ZfTypography>
                <ZfStack direction="row" justify="space-between">
                    <ZfSelectionControlGroup
                        label={t('contentAdmin.pickEditorMode')}
                        selectionType="radio"
                        onGroupValueChange={(e) => setEditorMode(e.detail as 'edit' | 'sort')}
                    >
                        <ZfStack direction="row" spacing="small">
                            <ZfRadio value="edit" label={t('contentAdmin.addRemoveItems')} checked={editorMode === 'edit'} />
                            <ZfRadio value="sort" label={t('contentAdmin.changeItemOrder')} checked={editorMode === 'sort'} />
                        </ZfStack>
                    </ZfSelectionControlGroup>
                    {editorMode === 'sort' && (
                        <ZfTextButton
                            hierarchy="secondary"
                            onClick={() => reorderTopic(unsavedTopic!)}
                            disabled={!hasUnsavedChanges}
                            style={{ alignSelf: 'flex-end' }}
                        >
                            {t('contentAdmin.saveOrder')}
                        </ZfTextButton>
                    )}
                </ZfStack>
                {editorMode === 'edit' ? (
                    <AdminTopicEditList
                        topic={topic}
                        onCreatePointClick={() =>
                            navigate({
                                pathname: '../../talking-points',
                                search: createSearchParams({ point: (-1).toString() }).toString(),
                            })
                        }
                    />
                ) : (
                    <AdminTopicSortList items={unsavedTopic?.talkingPoints ?? []} onChange={onTopicChange} />
                )}
                {showRootNameModal && (
                    <EditTopicNameModal
                        title={t('contentAdmin.editTopicName')}
                        isOpen={showRootNameModal}
                        name={topic.name}
                        onClose={() => setShowRootNameModal(false)}
                        onSave={(name) => updateTopic({ ...topic, name })}
                    />
                )}
            </ZfStack>
        </AdminContentBox>
    );
};
